/**
 * Rule-preview artifact — the `compose_rule` capability tool's chat surface
 * (TV1-2340 · P4).
 *
 * `compose_rule` asks the model for a rule GRAPH from prose and streams the
 * result back as an opaque `tool_result`. This module owns the two things that
 * must NOT live in the renderer:
 *
 *  1. the wire type (`CandidateGraph`) — declared locally, deliberately. The
 *     tool lives in a BACKEND package and the web-app must not import from one;
 *     the shape is a superset-compatible match for the rule editor's own
 *     `RawDsl` (`teselly-rule-editor/src/dsl/parse.ts`), which is why the graph
 *     object is handed to `RuleNodesView` verbatim with no adapter.
 *  2. the dangling-edge guard — see `findDanglingEdgeTargets`.
 *
 * ⚠️ DEMO SCOPE. Nothing on this path persists, activates, dry-runs or executes
 * a rule. The flow ends at a rendered graph, and the card's copy must keep
 * saying so — a seller who reads "rule created" and stops looking is the one
 * failure mode this artifact can cause.
 */

/** Block type string this artifact registers under. Single source of truth. */
export const RULE_PREVIEW_BLOCK_TYPE = 'rule-preview';

/**
 * The rule-editor wire format, restated locally.
 *
 * Kept structurally identical to `RawDsl` — `nodes[]` with an OPTIONAL `id`,
 * free-form `values` / `inputsConfig`, and outbound `edges[]` addressed by a
 * `to` string. `declaredInputs` rides along untouched.
 */
export type CandidateGraphEdge = {
  to: string;
  fromOutput?: string;
  toInput?: string;
  optional?: boolean;
};

export type CandidateGraphNode = {
  type: string;
  id?: string;
  values?: Record<string, unknown>;
  inputsConfig?: Record<string, unknown>;
  edges?: CandidateGraphEdge[];
};

export type CandidateGraph = {
  nodes: CandidateGraphNode[];
  declaredInputs?: Array<{ nodeId: string; inputName: string }>;
};

/**
 * What the card renders. Three terminal states, and the third exists ONLY
 * because the editor throws rather than degrades — see below.
 */
export type RulePreviewPayload =
  /** The composer produced a graph the editor can parse. */
  | { status: 'graph'; graph: CandidateGraph; attempts: number; repaired: boolean }
  /** The composer refused, with a seller-facing Spanish explanation. */
  | { status: 'gap'; gapDescription: string; attempts: number }
  /**
   * The composer produced a graph that would CRASH the editor. Carries the
   * offending targets so the card can say which reference broke, instead of
   * "something went wrong".
   */
  | { status: 'invalid'; reason: 'dangling-edge'; targets: string[]; attempts: number };

const isRecord = ( value: unknown ): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * A graph worth rendering: an object with at least one node. Mirrors
 * `isRenderableReport`'s discipline — a `{ nodes: [] }` that slipped through
 * would mount an empty canvas, which reads as "broken", not as a rule.
 *
 * Written as a type predicate rather than a cast so the narrowing is the
 * check's own product: `as unknown as CandidateGraph` would assert the exact
 * thing this function exists to verify.
 */
const isRenderableGraph = ( value: unknown ): value is CandidateGraph =>
  isRecord( value ) && Array.isArray( value.nodes ) && value.nodes.length > 0;

/**
 * Mirror of the editor's `assignUniqueIds` (`parse.ts:27-59`): a node's id is
 * its explicit `id` or, failing that, its bare `type`; when two or more nodes
 * collapse onto the same preliminary id the FIRST keeps the bare name and the
 * rest are suffixed `-1`, `-2`, …
 *
 * Re-implemented rather than imported because `parse.ts` does not export it,
 * and guessing the rule is exactly how a guard ends up rejecting a graph the
 * editor would have rendered fine (or, worse, passing one it cannot).
 */
export const assignedNodeIds = ( nodes: CandidateGraphNode[]): string[] => {
  const preliminary = nodes.map(( n ) => n.id || n.type );

  const occurrences = new Map<string, number>();
  for ( const id of preliminary ) {
    occurrences.set( id, ( occurrences.get( id ) || 0 ) + 1 );
  }

  const seen = new Map<string, number>();
  return preliminary.map(( baseId ) => {
    if (( occurrences.get( baseId ) || 1 ) <= 1 ) return baseId;
    const n = seen.get( baseId ) || 0;
    seen.set( baseId, n + 1 );
    return n === 0 ? baseId : `${baseId}-${n}`;
  });
};

/**
 * 🚨 The crash guard. `parseDsl` THROWS `Edge target not found: <to>` when an
 * edge names nothing — and `unknown-edge-target` is the composer's single most
 * common failure (32 occurrences across the recorded 20-case run). An unguarded
 * mount therefore takes the whole chat panel down mid-conversation.
 *
 * Resolution mirrors the editor's `buildResolver` (`parse.ts:61-71`) EXACTLY:
 * a `to` resolves if it matches an assigned node id **or** a node `type`. The
 * type fallback is not decoration — the model routinely addresses nodes by type
 * and those graphs render correctly, so a guard checking ids alone would reject
 * a large share of the good ones.
 *
 * Returns every unresolvable target (deduped, in order) — empty means safe.
 */
export const findDanglingEdgeTargets = ( graph: CandidateGraph ): string[] => {
  const resolvable = new Set<string>([
    ...assignedNodeIds( graph.nodes ),
    ...graph.nodes.map(( n ) => n.type )
  ]);

  const dangling: string[] = [];
  for ( const node of graph.nodes ) {
    for ( const edge of node.edges ?? []) {
      const to = edge?.to;
      // A non-string `to` is just as fatal — `find(( n ) => n.id === to )`
      // matches nothing and the resolver throws all the same.
      const key = typeof to === 'string' ? to : String( to );
      if ( !resolvable.has( key ) && !dangling.includes( key )) dangling.push( key );
    }
  }
  return dangling;
};

/**
 * Narrow the opaque `compose_rule` tool result into a renderable payload.
 *
 * Mirrors `toReportPreview`'s discipline: the backend tool has ALREADY decided
 * ok/gap, so this is shape-narrowing, not re-classification. Anything it does
 * not recognise returns `undefined` and the message renders as plain text — a
 * malformed payload must never throw on the render path.
 *
 * The one judgement it does make is the `graph` -> `invalid` downgrade: a graph
 * carrying a dangling edge is a payload the editor CANNOT draw, so it is
 * classified here, once, rather than left for a renderer to discover by
 * crashing.
 */
export const toRulePreviewPayload = ( result: unknown ): RulePreviewPayload | undefined => {
  if ( !isRecord( result )) return undefined;

  const attempts = typeof result.attempts === 'number' ? result.attempts : 1;

  if ( result.ok === true ) {
    const graph = result.graph;
    if ( !isRenderableGraph( graph )) return undefined;

    const dangling = findDanglingEdgeTargets( graph );
    if ( dangling.length > 0 ) {
      return { status: 'invalid', reason: 'dangling-edge', targets: dangling, attempts };
    }
    return { status: 'graph', graph, attempts, repaired: result.repaired === true };
  }

  if ( result.ok === false ) {
    const gapDescription = typeof result.gapDescription === 'string' ? result.gapDescription : '';
    // No explanation means the card would render an empty apology. The
    // assistant's own message text is the honest answer on its own.
    if ( !gapDescription ) return undefined;
    return { status: 'gap', gapDescription, attempts };
  }

  return undefined;
};
